import { useState, useEffect } from "react";
import { fetchAndCacheImage, removeImageBackgroundBlob } from "src/utils/Utils.js";
import * as imageCacheStorage from "src/utils/imageCacheStorage.js";
import { canonicalImageKey } from "src/utils/imageUrl.js";

/** How long a processing pass (cache read + fetch + canvas) may run before the
 *  consumer falls back to the raw source URL. The shared request keeps running
 *  and still populates the cache if it settles later. */
const PROCESSING_TIMEOUT_MS = 5000;

/** Rejection marker distinguishing a timed-out pass from a genuine failure. */
const TIMEOUT_SENTINEL = Symbol("image-processing-timeout");

/** Shared so an unset source settles on one reference React can bail out on. */
const EMPTY_RESOLUTION = { uri: null, source: null };

/** Module-level cache: normalized URL → processed image entry. Survives component unmounts. */
const processedImageCache = new Map();
/**
 * Consumers that asked for a derivative before its shared request created an
 * entry. Keeping the claim here closes the gap between request start and cache
 * insertion, where an LRU eviction could otherwise revoke a URL before its
 * first mounted consumer acquired it.
 */
const pendingProcessedImageClaims = new Map();
/** In-flight work per key, so concurrent mounts share one canvas pass. */
const processedRequests = new Map();
export const MAX_PROCESSED_ENTRIES = 300;

/** Release browser-managed bytes only for object URLs this cache created. */
const revokeProcessedObjectUrl = (uri) => {
  if (typeof uri === "string" && uri.startsWith("blob:")) {
    URL.revokeObjectURL(uri);
  }
};

/** Release an entry only once neither the cache nor a mounted hook owns it. */
const releaseProcessedImageEntry = (entry) => {
  if (!entry.cached && entry.consumerCount === 0) {
    revokeProcessedObjectUrl(entry.uri);
  }
};

/** Retain a cached entry for the lifetime of one mounted hook. */
const retainCachedProcessedImage = (key) => {
  const entry = processedImageCache.get(key);
  if (!entry) return () => {};

  entry.consumerCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    entry.consumerCount -= 1;
    releaseProcessedImageEntry(entry);
  };
};

/**
 * Reserve a mounted consumer before the async request creates its cache entry.
 * The returned cleanup is safe whether the request resolves before or after
 * the component unmounts.
 */
const reserveProcessedImage = (key) => {
  const cachedEntry = processedImageCache.get(key);
  if (cachedEntry) return retainCachedProcessedImage(key);

  const claim = { entry: null };
  const claims = pendingProcessedImageClaims.get(key) ?? new Set();
  claims.add(claim);
  pendingProcessedImageClaims.set(key, claims);

  let released = false;
  return () => {
    if (released) return;
    released = true;

    if (claim.entry) {
      claim.entry.consumerCount -= 1;
      releaseProcessedImageEntry(claim.entry);
      return;
    }

    claims.delete(claim);
    if (!claims.size) pendingProcessedImageClaims.delete(key);
  };
};

/**
 * Bounded insert. Eviction only drops cache ownership: a blob URL is revoked
 * after the final mounted consumer releases its claim.
 */
const rememberProcessedImage = (key, uri) => {
  const existingEntry = processedImageCache.get(key);
  if (existingEntry) {
    processedImageCache.delete(key);
    existingEntry.cached = false;
    releaseProcessedImageEntry(existingEntry);
  }

  const entry = { uri, cached: true, consumerCount: 0 };
  const claims = pendingProcessedImageClaims.get(key);
  if (claims) {
    claims.forEach((claim) => {
      claim.entry = entry;
      entry.consumerCount += 1;
    });
    pendingProcessedImageClaims.delete(key);
  }
  processedImageCache.set(key, entry);

  while (processedImageCache.size > MAX_PROCESSED_ENTRIES) {
    const evictedKey = processedImageCache.keys().next().value;
    const evictedEntry = processedImageCache.get(evictedKey);
    processedImageCache.delete(evictedKey);
    evictedEntry.cached = false;
    releaseProcessedImageEntry(evictedEntry);
  }
};
/**
 * Synchronous cache lookup — safe to call outside hooks (e.g. in useState initializers).
 *
 * Prefers a URL the service worker can serve, because that puts the bytes on the
 * browser's own image path: no cache read and no object URL on the main thread,
 * so the first render can already paint the final art.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
export const getProcessedImage = (url) => {
  if (!url) return null;
  return (
    processedImageCache.get(canonicalImageKey(url))?.uri ??
    imageCacheStorage.processedImageUrl(url)
  );
};

/**
 * Drops the in-memory derivative for an image whose source changed, so the next
 * consumer rebuilds it. Mounted consumers keep the URL they already hold.
 * @param {string|null} url
 */
export const forgetProcessedImage = (url) => {
  const key = canonicalImageKey(url);
  const entry = key ? processedImageCache.get(key) : undefined;
  if (!entry) return;

  processedImageCache.delete(key);
  processedRequests.delete(key);
  entry.cached = false;
  releaseProcessedImageEntry(entry);
};

/**
 * Reads the derivative from Cache Storage, or builds and persists it.
 * @param {string} imageUrl
 * @param {string} key
 * @returns {Promise<string>} Object URL of the background-removed image.
 */
const resolveProcessedImage = async (imageUrl, key) => {
  const persisted = await imageCacheStorage.getProcessed(imageUrl);
  if (persisted) {
    // Re-record it: finding a derivative the synchronous index had forgotten is
    // how that index heals, and the next visit gets the worker path back.
    imageCacheStorage.rememberProcessed(imageUrl);
    imageCacheStorage.revalidateProcessed(imageUrl);
    const uri = URL.createObjectURL(persisted.blob);
    rememberProcessedImage(key, uri);
    return uri;
  }

  // The fetched blob only feeds the canvas — release it once the processed
  // copy exists, or it is retained for the lifetime of the document.
  let fetchedUri;
  try {
    fetchedUri = await fetchAndCacheImage(imageUrl);
    // Read before the canvas pass, so a source replaced while it runs is caught.
    const sourceHash = await imageCacheStorage.sourceHashFor(imageUrl, fetchedUri);
    const blob = await removeImageBackgroundBlob(fetchedUri);
    const uri = URL.createObjectURL(blob);
    rememberProcessedImage(key, uri);
    imageCacheStorage.putProcessed(imageUrl, blob, { sourceHash });
    return uri;
  } finally {
    if (fetchedUri) URL.revokeObjectURL(fetchedUri);
  }
};

/**
 * Hook to process an image URL by fetching, caching, and removing background.
 * Results are stored in a module-level cache so subsequent mounts skip the async work.
 * @param {string|null} imageUrl - The URL of the image to process
 * @returns {{ processedImage: string|null, loading: boolean, source: string|null,
 *   derived: boolean }} `source` is the URL `processedImage` was derived from,
 *   which lags `imageUrl` while a new source is being processed. `derived` is
 *   false when the pipeline gave up and handed back the raw source.
 */
export const useProcessedImage = (imageUrl) => {
  const cached = getProcessedImage(imageUrl);

  // The uri and the source it came from move together: a consumer that keys a
  // persistent cache on the source must never pair it with an older derivative.
  const [resolved, setResolved] = useState({
    uri: cached,
    source: cached ? imageUrl : null,
  });
  const [loading, setLoading] = useState(!!imageUrl && !cached);

  useEffect(() => {
    if (!imageUrl) {
      setResolved((current) =>
        current.uri === null && current.source === null ? current : EMPTY_RESOLUTION,
      );
      setLoading(false);
      return;
    }

    const key = canonicalImageKey(imageUrl);

    // A worker-served URL needs no work at all: the browser fetches it like any
    // other image, so there is nothing to await and nothing to reveal later.
    const ready = getProcessedImage(imageUrl);
    if (ready) {
      // Only a worker-served derivative comes from an earlier page load; one in
      // memory was built or already checked during this one.
      if (!processedImageCache.has(key)) {
        imageCacheStorage.revalidateProcessed(imageUrl);
      }
      const release = retainCachedProcessedImage(key);
      setResolved({ uri: ready, source: imageUrl });
      setLoading(false);
      return release;
    }

    let isMounted = true;
    setLoading(true);
    const release = reserveProcessedImage(key);

    let request = processedRequests.get(key);
    if (!request) {
      request = resolveProcessedImage(imageUrl, key).finally(() => {
        if (processedRequests.get(key) === request) {
          processedRequests.delete(key);
        }
      });
      processedRequests.set(key, request);
    }

    // A hung layer (Cache Storage read, fetch, canvas decode) never rejects on
    // its own — race it against a timeout so this consumer always settles and
    // degrades to the raw source instead of pinning its tile's loading state.
    // Only this consumer settles on timeout: the shared request keeps running
    // and still writes the real derivative to the cache when it completes, so
    // a transient stall must not poison the cache with the raw source.
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(TIMEOUT_SENTINEL), PROCESSING_TIMEOUT_MS);
    });

    Promise.race([request, timeout])
      .catch((error) => {
        if (error !== TIMEOUT_SENTINEL) {
          console.error("Error processing image:", error);
          rememberProcessedImage(key, imageUrl);
        }
        return imageUrl;
      })
      .then((uri) => {
        clearTimeout(timer);
        if (!isMounted) return;
        setResolved({ uri, source: imageUrl });
        setLoading(false);
      });

    return () => {
      isMounted = false;
      clearTimeout(timer);
      release();
    };
  }, [imageUrl]);

  return {
    processedImage: resolved.uri,
    loading,
    source: resolved.source,
    // Timeout and error both fall back to the raw source, whose bytes still
    // carry the background the derivative would have removed.
    derived: !!resolved.uri && resolved.uri !== resolved.source,
  };
};
